import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createPortal } from 'react-dom';
import AutoStatusToast from '../components/common/AutoStatusToast';
import MemberProfileThumb from '../components/common/MemberProfileThumb';
import ClassMemberOrderNumberInput from '../components/classMembers/ClassMemberOrderNumberInput';
import ClassMemberSeatAssignmentModal from '../components/classMembers/ClassMemberSeatAssignmentModal';
import ProfileImageSelectModal from '../components/classMembers/ProfileImageSelectModal';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { getClassMemberGradeLayoutsMap, replaceGradeMemberLayout } from '../services/classMembersService';
import {
  getMemberOwnedDataSummary,
  memberOwnedDataHasContent,
} from '../services/classMemberOwnedDataService';
import { Grade } from '../types/domain';
import { displayClassMemberName } from '../utils/classMemberNameDisplay';
import {
  findDuplicateMemberNumbers,
  getClassMemberDisplayNumbers,
  groupHasFixedMemberNumbers,
  materializeMemberNumbers,
  nextMemberStudentNumber,
  reorderMembersKeepingNumbers,
  sortMembersByDisplayNumber,
} from '../utils/classMemberNumbers';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import {
  type ClassMemberGradeLayout,
  type ClassMemberGroupMeta,
  type ClassMemberRow,
  type ClassMemberGender,
  DEFAULT_MEMBER_PROFILE_IMAGE,
  MEMBER_ENTRY_NUMBER_LENGTH,
  MEMBER_GROUP_NO_MAX,
  MEMBER_GROUP_NO_MIN,
  emptyGradeLayout,
  generateMemberEntryNumber,
  loadClassMemberGradeLayoutFromStorage,
  normalizeEntryNumber,
  normalizeMemberGroupNo,
} from '../utils/classMembersStorage';
import {
  loadSelectedClassGroupId,
  saveSelectedClassGroupId,
  subscribeSelectedClassGroupChanged,
} from '../utils/selectedClassGroupPreference';

const MEMBER_GROUP_NO_OPTIONS = Array.from(
  { length: MEMBER_GROUP_NO_MAX - MEMBER_GROUP_NO_MIN + 1 },
  (_, i) => MEMBER_GROUP_NO_MIN + i
);

const MEMBER_OWNED_DATA_DELETE_MESSAGE =
  '이 멤버에 포인트·코인·메모·아이템·과제(배정·보상)·프로필 카드·프로필 사진 정보가 있습니다. 삭제하면 해당 정보가 모두 삭제됩니다. 계속할까요?';

const GROUP_OWNED_DATA_DELETE_MESSAGE =
  '이 학급의 멤버에 포인트·코인·메모·아이템·과제(배정·보상)·프로필 카드·프로필 사진 정보가 있습니다. 학급을 삭제하면 해당 정보가 모두 삭제됩니다. 계속할까요?';

interface ClassMembersPageProps {
  workspaceId: string;
  currentGradeId?: string;
  grades?: Grade[];
  onSwitchToClassIntegrated?: () => void;
  /** 계정 메뉴 대형 모달 임베드: 페이지 크롬·이탈 dirty 등록 생략 */
  embeddedInModal?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  /** 「+ 학급」 버튼을 렌더할 외부 슬롯(모달 헤더 등). 없으면 페이지 헤더 슬롯 사용 */
  addGroupHost?: HTMLElement | null;
  saveActionsRef?: React.MutableRefObject<{
    save: () => Promise<void>;
    discard?: () => void;
  } | null>;
}

function generateMemberId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function generateGroupId(): string {
  return `cmg-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function normalizeGender(raw: string | undefined): ClassMemberGender {
  const t = (raw ?? '').trim().toLowerCase();
  if (!t) return '';
  if (t === '남' || t === '남자' || t === 'm' || t === 'male') return '남';
  if (t === '여' || t === '여자' || t === 'f' || t === 'female') return '여';
  return '';
}

/** 엑셀 등 탭/줄 단위 텍스트 → 이름·성별 행 목록 */
function parseClassMemberPasteText(raw: string): { name: string; gender: ClassMemberGender }[] {
  const text = raw.replace(/^\uFEFF/, '');
  const lines = text.split(/\r\n|\n|\r/);
  const out: { name: string; gender: ClassMemberGender }[] = [];
  for (const line of lines) {
    const trimmed = line.trimEnd();
    if (!trimmed.trim()) continue;
    const parts = trimmed.split('\t');
    const name = (parts[0] ?? '').trim();
    const gender = normalizeGender(parts[1]);
    out.push({ name, gender });
  }
  return out;
}

/** 한 칸이 아니라 여러 행/열 붙여넣기인지 (브라우저 기본 붙여넣기와 구분) */
function shouldHandleBulkPaste(text: string): boolean {
  if (text.includes('\t')) return true;
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim());
  return lines.length >= 2;
}

function applyParsedMembers(
  rows: ClassMemberRow[],
  startIndex: number,
  parsed: { name: string; gender: ClassMemberGender }[]
): ClassMemberRow[] {
  const next = [...rows];
  for (let i = 0; i < parsed.length; i += 1) {
    const idx = startIndex + i;
    const p = parsed[i];
    if (idx < next.length) {
      next[idx] = { ...next[idx], name: p.name, gender: p.gender };
    } else {
      next.push({
        id: generateMemberId(),
        name: p.name,
        gender: p.gender,
        groupNo: null,
        entryNumber: generateMemberEntryNumber(),
        profileImage: DEFAULT_MEMBER_PROFILE_IMAGE,
      });
    }
  }
  return next;
}

const NAME_CHOSEONG_SLOTS = [
  { index: 1, label: '첫 글자' },
  { index: 2, label: '둘째 글자' },
  { index: 3, label: '셋째 글자' },
  { index: 4, label: '넷째 글자' },
] as const;

/** 선택 멤버용 6자리 입장번호 — 미선택 멤버·서로 간 중복 회피 */
function generateUniqueEntryNumbers(count: number, reserved: Set<string>): string[] {
  const out: string[] = [];
  const used = new Set(reserved);
  let guard = 0;
  while (out.length < count && guard < 20000) {
    guard += 1;
    const n = generateMemberEntryNumber();
    if (used.has(n)) continue;
    used.add(n);
    out.push(n);
  }
  return out;
}

/** 고정 번호를 쓰는 그룹은 저장 시 모든 멤버 번호를 확정(번호 없는 멤버 = 최대+1부터). 예전 그룹은 그대로. */
function materializeLayoutMemberNumbers(layout: ClassMemberGradeLayout): ClassMemberGradeLayout {
  let changed = false;
  const membersByGroupId: Record<string, ClassMemberRow[]> = {};
  for (const [gid, list] of Object.entries(layout.membersByGroupId)) {
    if (groupHasFixedMemberNumbers(list ?? [])) {
      const next = materializeMemberNumbers(list);
      if (next.some((r, i) => r !== list[i])) changed = true;
      membersByGroupId[gid] = next;
    } else {
      membersByGroupId[gid] = list;
    }
  }
  return changed ? { ...layout, membersByGroupId } : layout;
}

const SortableMemberTableRow: React.FC<{
  id: string;
  orderIndex: number;
  /** 화면에 보이는 고정 번호(공통 헬퍼로 계산) */
  displayNumber: number;
  /** 같은 그룹에 같은 번호가 있는지 */
  numberDuplicate: boolean;
  member: ClassMemberRow;
  groupMembers: ClassMemberRow[];
  workspaceId: string;
  gradeId: string;
  disabled: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onNameChange: (name: string) => void;
  onEntryNumberChange: (entryNumber: string) => void;
  onGenderChange: (gender: ClassMemberRow['gender']) => void;
  onGroupNoChange: (groupNo: number | null) => void;
  /** 프로필 이미지 설정 확인 — 변경된 학생만 { memberId: imageId } */
  onProfileImagesChange: (changes: Record<string, string>) => void;
  onMembersPhotoChange: (nextMembers: ClassMemberRow[]) => void;
  onRemove: () => void;
  onNumberCommit: (studentNumber: number) => void;
  onBulkPasteFromName: (startIndex: number, parsed: { name: string; gender: ClassMemberGender }[]) => void;
}> = ({
  id,
  orderIndex,
  displayNumber,
  numberDuplicate,
  member,
  groupMembers,
  workspaceId,
  gradeId,
  disabled,
  selected,
  onToggleSelect,
  onNameChange,
  onEntryNumberChange,
  onGenderChange,
  onGroupNoChange,
  onProfileImagesChange,
  onMembersPhotoChange,
  onRemove,
  onNumberCommit,
  onBulkPasteFromName,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleNamePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text/plain');
    if (!shouldHandleBulkPaste(text)) return;
    e.preventDefault();
    const parsed = parseClassMemberPasteText(text);
    if (!parsed.length) return;
    onBulkPasteFromName(orderIndex, parsed);
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={[
        'cp-class-members-row',
        isDragging ? 'is-dragging' : '',
        selected ? 'is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <td className="cp-class-members-col-select">
        <button
          type="button"
          className={
            selected
              ? 'cp-class-members-select-circle is-checked'
              : 'cp-class-members-select-circle'
          }
          aria-pressed={selected}
          aria-label={`멤버 ${displayNumber}번 선택`}
          disabled={disabled}
          onClick={onToggleSelect}
        />
      </td>
      <td className="cp-class-members-col-num">
        <ClassMemberOrderNumberInput
          value={displayNumber}
          duplicate={numberDuplicate}
          disabled={disabled}
          onCommit={onNumberCommit}
        />
      </td>
      <td className="cp-class-members-col-name">
        <input
          type="text"
          className="cp-class-members-input"
          value={
            member.nameChoseongPositions?.length
              ? displayClassMemberName(member)
              : member.name
          }
          onChange={(e) => onNameChange(e.target.value)}
          onPaste={handleNamePaste}
          placeholder="이름"
          disabled={disabled}
          readOnly={Boolean(member.nameChoseongPositions?.length)}
          title={
            member.nameChoseongPositions?.length
              ? '초성으로 표시 중입니다. 이름 초성 바꾸기에서 칸을 모두 해제하고 적용하면 원래 이름이 돌아옵니다.'
              : undefined
          }
          aria-label={`멤버 ${orderIndex + 1} 이름`}
        />
      </td>
      <td className="cp-class-members-col-gender">
        <select
          className="cp-class-members-select"
          value={member.gender}
          onChange={(e) => onGenderChange(e.target.value as ClassMemberRow['gender'])}
          disabled={disabled}
          aria-label={`멤버 ${orderIndex + 1} 성별`}
        >
          <option value="">선택</option>
          <option value="남">남</option>
          <option value="여">여</option>
        </select>
      </td>
      <td className="cp-class-members-col-group">
        <select
          className="cp-class-members-select"
          value={member.groupNo == null ? '' : String(member.groupNo)}
          onChange={(e) => {
            const raw = e.target.value;
            onGroupNoChange(raw === '' ? null : normalizeMemberGroupNo(raw));
          }}
          disabled={disabled}
          aria-label={`멤버 ${orderIndex + 1} 그룹`}
        >
          <option value="">없음</option>
          {MEMBER_GROUP_NO_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </td>
      <td className="cp-class-members-col-entry">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={MEMBER_ENTRY_NUMBER_LENGTH}
          className="cp-class-members-input cp-class-members-entry-input"
          value={member.entryNumber ?? ''}
          onChange={(e) =>
            onEntryNumberChange(e.target.value.replace(/\D/g, '').slice(0, MEMBER_ENTRY_NUMBER_LENGTH))
          }
          onBlur={() => onEntryNumberChange(normalizeEntryNumber(member.entryNumber))}
          placeholder="자동"
          disabled={disabled}
          aria-label={`멤버 ${orderIndex + 1} 입장번호`}
          title="입장번호 (6자리 숫자, 000000 제외). 비우면 저장 시 자동 발급"
        />
      </td>
      <td className="cp-class-members-col-profile">
        <button
          type="button"
          className="cp-class-members-profile-btn"
          disabled={disabled}
          title="프로필 이미지 선택"
          aria-label={`멤버 ${orderIndex + 1} 프로필 이미지`}
          onClick={() => setProfileModalOpen(true)}
        >
          <MemberProfileThumb
            member={member}
            workspaceId={workspaceId}
            gradeId={gradeId}
            className="cp-class-members-profile-thumb"
            fallback={
              <span className="cp-class-members-profile-fallback">
                {(displayClassMemberName(member) || '?').slice(0, 1)}
              </span>
            }
          />
        </button>
        <ProfileImageSelectModal
          open={profileModalOpen}
          initialMemberId={member.id}
          members={groupMembers}
          workspaceId={workspaceId}
          gradeId={gradeId}
          onMembersPhotoChange={onMembersPhotoChange}
          onClose={() => setProfileModalOpen(false)}
          onConfirm={onProfileImagesChange}
        />
      </td>
      <td className="cp-class-members-col-drag">
        <button
          type="button"
          className="cp-class-members-grip-btn"
          aria-label="순서 변경"
          title="드래그하여 순서 변경 (지금 쓰는 번호를 새 순서대로 다시 붙여요)"
          disabled={disabled}
          {...attributes}
          {...listeners}
        >
          <img src="/GripVertical.svg" alt="" className="track-icon" />
        </button>
      </td>
      <td className="cp-class-members-col-remove">
        <button
          type="button"
          className="cp-class-members-remove-btn"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`멤버 ${displayNumber}번 삭제`}
        >
          ×
        </button>
      </td>
    </tr>
  );
};

const ClassMembersGroupSection: React.FC<{
  workspaceId: string;
  grade: Grade;
  layout: ClassMemberGradeLayout;
  group: ClassMemberGroupMeta;
  onRequestRemoveGroup: () => void;
  groupDeleteChecking: boolean;
  onLayoutChange: (next: ClassMemberGradeLayout) => void;
  onPersistLayout: (next: ClassMemberGradeLayout) => Promise<void>;
}> = ({
  workspaceId,
  grade,
  layout,
  group,
  onRequestRemoveGroup,
  groupDeleteChecking,
  onLayoutChange,
  onPersistLayout,
}) => {
  const selectedGroupId = group.id;
  const parsed = parseGradeDisplayName(grade.name);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const [seatModalOpen, setSeatModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    kind: 'member';
    message: string;
    memberId?: string;
  } | null>(null);
  const [deleteChecking, setDeleteChecking] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(() => new Set());
  const [bulkGenderModalOpen, setBulkGenderModalOpen] = useState(false);
  const [bulkGenderDraft, setBulkGenderDraft] = useState<Exclude<ClassMemberGender, ''>>('남');
  const [bulkEntryModalOpen, setBulkEntryModalOpen] = useState(false);
  const [bulkEntryPreview, setBulkEntryPreview] = useState<{ id: string; name: string; entryNumber: string }[]>(
    []
  );
  const [bulkEntryBatchInput, setBulkEntryBatchInput] = useState('');
  const [bulkNameModalOpen, setBulkNameModalOpen] = useState(false);
  const choseongSnapshotRef = useRef<Map<string, number[] | undefined>>(new Map());
  const rowsRef = useRef<ClassMemberRow[]>([]);

  const rows = useMemo(
    () => (selectedGroupId ? layout.membersByGroupId[selectedGroupId] ?? [] : []),
    [layout.membersByGroupId, selectedGroupId]
  );
  rowsRef.current = rows;
  const itemIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const displayNumbers = useMemo(() => getClassMemberDisplayNumbers(rows), [rows]);
  const duplicateNumbers = useMemo(() => findDuplicateMemberNumbers(rows), [rows]);

  useEffect(() => {
    setSelectedMemberIds(new Set());
    setBulkGenderModalOpen(false);
    setBulkEntryModalOpen(false);
    setBulkNameModalOpen(false);
  }, [selectedGroupId]);

  useEffect(() => {
    if (!bulkNameModalOpen) return undefined;
    const panel = document.querySelector('.cp-class-members-settings-modal-panel');
    if (!(panel instanceof HTMLElement)) return undefined;
    panel.classList.add('is-choseong-lock');
    return () => {
      panel.classList.remove('is-choseong-lock');
    };
  }, [bulkNameModalOpen]);

  useEffect(() => {
    setSelectedMemberIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(rows.map((r) => r.id));
      const next = new Set(Array.from(prev).filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const selectedCount = selectedMemberIds.size;
  const allSelected = rows.length > 0 && rows.every((r) => selectedMemberIds.has(r.id));
  const bulkActionsEnabled = selectedCount > 0 && !deleteChecking;

  const toggleMemberSelect = useCallback((id: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedMemberIds((prev) => {
      if (rows.length === 0) return prev;
      if (rows.every((r) => prev.has(r.id))) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }, [rows]);

  const openBulkGenderModal = useCallback(() => {
    if (selectedMemberIds.size === 0) return;
    setBulkGenderDraft('남');
    setBulkGenderModalOpen(true);
  }, [selectedMemberIds]);

  const openBulkEntryModal = useCallback(() => {
    if (selectedMemberIds.size === 0) return;
    const selectedRows = rows.filter((r) => selectedMemberIds.has(r.id));
    setBulkEntryPreview(
      selectedRows.map((r) => ({
        id: r.id,
        name: displayClassMemberName(r).trim() || `멤버 ${rows.findIndex((x) => x.id === r.id) + 1}`,
        entryNumber: normalizeEntryNumber(r.entryNumber),
      }))
    );
    setBulkEntryBatchInput('');
    setBulkEntryModalOpen(true);
  }, [rows, selectedMemberIds]);

  const reflectBulkEntryBatch = useCallback(() => {
    const next = normalizeEntryNumber(bulkEntryBatchInput);
    setBulkEntryPreview((prev) => prev.map((row) => ({ ...row, entryNumber: next })));
  }, [bulkEntryBatchInput]);

  const regenerateBulkEntryPreview = useCallback(() => {
    const reserved = new Set(
      rows
        .filter((r) => !selectedMemberIds.has(r.id))
        .map((r) => normalizeEntryNumber(r.entryNumber))
    );
    const generated = generateUniqueEntryNumbers(bulkEntryPreview.length, reserved);
    setBulkEntryPreview((prev) =>
      prev.map((row, i) => ({
        ...row,
        entryNumber: generated[i] ?? generateMemberEntryNumber(),
      }))
    );
  }, [bulkEntryPreview.length, rows, selectedMemberIds]);

  const persistRows = useCallback(
    (next: ClassMemberRow[]) => {
      if (!selectedGroupId) return;
      onLayoutChange({
        ...layout,
        membersByGroupId: { ...layout.membersByGroupId, [selectedGroupId]: next },
      });
    },
    [layout, onLayoutChange, selectedGroupId]
  );

  const applyBulkGender = useCallback(() => {
    if (selectedMemberIds.size === 0) return;
    persistRows(
      rows.map((r) => (selectedMemberIds.has(r.id) ? { ...r, gender: bulkGenderDraft } : r))
    );
    setBulkGenderModalOpen(false);
  }, [bulkGenderDraft, persistRows, rows, selectedMemberIds]);

  const openBulkNameModal = useCallback(() => {
    if (selectedMemberIds.size === 0) return;
    const snap = new Map<string, number[] | undefined>();
    rows.forEach((r) => {
      if (!selectedMemberIds.has(r.id)) return;
      snap.set(r.id, r.nameChoseongPositions?.length ? [...r.nameChoseongPositions] : undefined);
    });
    choseongSnapshotRef.current = snap;
    setBulkNameModalOpen(true);
  }, [rows, selectedMemberIds]);

  const toggleBulkNamePosition = useCallback(
    (index: number) => {
      const memberIds = selectedMemberIds;
      const currentRows = rowsRef.current;
      const anyHas = currentRows.some(
        (r) => memberIds.has(r.id) && (r.nameChoseongPositions ?? []).includes(index)
      );
      persistRows(
        currentRows.map((r) => {
          if (!memberIds.has(r.id)) return r;
          const set = new Set(r.nameChoseongPositions ?? []);
          if (anyHas) set.delete(index);
          else set.add(index);
          const positions = Array.from(set).sort((a, b) => a - b);
          const next = { ...r };
          if (positions.length > 0) next.nameChoseongPositions = positions;
          else delete next.nameChoseongPositions;
          return next;
        })
      );
    },
    [persistRows, selectedMemberIds]
  );

  const closeChoseongModal = useCallback(
    (revert: boolean) => {
      if (revert) {
        const snap = choseongSnapshotRef.current;
        persistRows(
          rowsRef.current.map((r) => {
            if (!snap.has(r.id)) return r;
            const prev = snap.get(r.id);
            const next = { ...r };
            if (prev?.length) next.nameChoseongPositions = [...prev];
            else delete next.nameChoseongPositions;
            return next;
          })
        );
      }
      setBulkNameModalOpen(false);
    },
    [persistRows]
  );

  const applyBulkEntryNumbers = useCallback(() => {
    if (bulkEntryPreview.length === 0) return;
    const byId = new Map(bulkEntryPreview.map((p) => [p.id, p.entryNumber]));
    persistRows(
      rows.map((r) => {
        const next = byId.get(r.id);
        return next != null ? { ...r, entryNumber: normalizeEntryNumber(next) } : r;
      })
    );
    setBulkEntryModalOpen(false);
  }, [bulkEntryPreview, persistRows, rows]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !selectedGroupId) return;
      const oldIndex = rows.findIndex((r) => r.id === active.id);
      const newIndex = rows.findIndex((r) => r.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      // 고정 번호 그룹: 번호 묶음(예: 1~16, 18)은 그대로, 새 순서대로 다시 배정
      persistRows(reorderMembersKeepingNumbers(rows, oldIndex, newIndex));
    },
    [persistRows, rows, selectedGroupId]
  );

  /**
   * 번호 직접 입력: 멤버별 고정 번호로 저장(중간 빈 번호 허용, 다른 멤버 번호는 그대로).
   * 처음 수정할 때 그룹 전체의 현재 표시 번호를 먼저 확정해 두어 다른 멤버 번호가 밀리지 않게 하고,
   * 목록은 번호순으로 다시 정렬한다. 같은 번호는 잠시 허용(서로 바꾸기용)하되 빨갛게 표시하고 저장은 막는다.
   */
  const commitMemberNumber = useCallback(
    (memberId: string, studentNumber: number) => {
      if (!selectedGroupId) return;
      if (!rows.some((r) => r.id === memberId)) return;
      const next = materializeMemberNumbers(rows).map((r) =>
        r.id === memberId ? { ...r, studentNumber } : r
      );
      persistRows(sortMembersByDisplayNumber(next));
    },
    [persistRows, rows, selectedGroupId]
  );

  const addRow = useCallback(() => {
    if (!selectedGroupId) return;
    persistRows([
      ...rows,
      {
        id: generateMemberId(),
        name: '',
        gender: '' as const,
        groupNo: null,
        // 고정 번호 그룹이면 (가장 큰 번호 + 1)을 바로 부여
        ...(() => {
          const n = nextMemberStudentNumber(rows);
          return n != null ? { studentNumber: n } : {};
        })(),
        entryNumber: generateMemberEntryNumber(),
        profileImage: DEFAULT_MEMBER_PROFILE_IMAGE,
      },
    ]);
  }, [persistRows, rows, selectedGroupId]);

  const updateRow = useCallback(
    (
      id: string,
      patch: Partial<Pick<ClassMemberRow, 'name' | 'gender' | 'groupNo' | 'profileImage' | 'entryNumber'>>
    ) => {
      persistRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    },
    [persistRows, rows]
  );

  const applyRemoveMember = useCallback(
    (id: string) => {
      if (!selectedGroupId) return;
      const current = layout.membersByGroupId[selectedGroupId] ?? [];
      onLayoutChange({
        ...layout,
        membersByGroupId: {
          ...layout.membersByGroupId,
          [selectedGroupId]: current.filter((r) => r.id !== id),
        },
      });
    },
    [layout, onLayoutChange, selectedGroupId]
  );

  const removeRow = useCallback(
    (id: string) => {
      void (async () => {
        setDeleteChecking(true);
        try {
          const summary = await getMemberOwnedDataSummary(workspaceId, grade.id, [id]);
          if (memberOwnedDataHasContent(summary)) {
            setDeleteConfirm({
              kind: 'member',
              message: MEMBER_OWNED_DATA_DELETE_MESSAGE,
              memberId: id,
            });
            return;
          }
          applyRemoveMember(id);
        } catch {
          applyRemoveMember(id);
        } finally {
          setDeleteChecking(false);
        }
      })();
    },
    [applyRemoveMember, grade.id, workspaceId]
  );

  const handleBulkPasteFromName = useCallback(
    (startIndex: number, parsed: { name: string; gender: ClassMemberGender }[]) => {
      persistRows(applyParsedMembers(rows, startIndex, parsed));
    },
    [persistRows, rows]
  );

  const handleEmptyPasteBridge = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text/plain');
    if (!shouldHandleBulkPaste(text)) return;
    e.preventDefault();
    const parsed = parseClassMemberPasteText(text);
    if (!parsed.length || !selectedGroupId) return;
    persistRows(applyParsedMembers([], 0, parsed));
  };

  useEffect(() => {
    if (!renamingGroupId) return;
    const el = renameInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [renamingGroupId]);

  const commitGroupRename = useCallback(
    (groupId: string) => {
      const prev = layout.groups.find((x) => x.id === groupId)?.name ?? '';
      const nextName = renameDraft.trim() || prev;
      onLayoutChange({
        ...layout,
        groups: layout.groups.map((x) => (x.id === groupId ? { ...x, name: nextName } : x)),
      });
      setRenamingGroupId(null);
      setRenameDraft('');
    },
    [layout, onLayoutChange, renameDraft]
  );

  const cancelGroupRename = useCallback(() => {
    setRenamingGroupId(null);
    setRenameDraft('');
  }, []);

  const startGroupRename = useCallback((g: ClassMemberGroupMeta) => {
    setRenamingGroupId(g.id);
    setRenameDraft(g.name);
  }, []);

  const isRenaming = renamingGroupId === group.id;

  return (
    <article className="cp-card cp-class-members-section">
      <button
        type="button"
        className="cp-class-members-group-trash"
        aria-label={`${group.name} 삭제`}
        title="학급 삭제"
        disabled={groupDeleteChecking || deleteChecking}
        onClick={onRequestRemoveGroup}
      >
        <img src="/Trash.svg" alt="" className="cp-todo-note-trash-icon" />
      </button>
      <div className="cp-track-section-header cp-grade-track-header-row cp-class-members-section-header">
        <div className="cp-class-members-section-header-title-row">
          <div className="card-summary pill cp-track-class-pill">클래스 단독</div>
          <h3>클래스 멤버</h3>
          <div className="cp-grade-track-class-block cp-class-members-class-title">
            <span className="cp-grade-track-class-block-label">{parsed.title}</span>
            {parsed.grades.map((num) => (
              <span key={num} className="class-grade-chip-display">
                {num}
              </span>
            ))}
          </div>
        </div>
        <div className="cp-class-members-group-section-title">
          {isRenaming ? (
            <input
              ref={renameInputRef}
              type="text"
              className="cp-class-members-group-rename-input"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onBlur={() => commitGroupRename(group.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitGroupRename(group.id);
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelGroupRename();
                }
              }}
              aria-label="학급 이름"
            />
          ) : (
            <button
              type="button"
              className="cp-class-members-group-section-name"
              onDoubleClick={() => startGroupRename(group)}
              title="더블클릭하면 이름을 수정합니다"
            >
              {group.name}
            </button>
          )}
          <span className="cp-class-members-group-block-count">총 {rows.length}명</span>
        </div>
      </div>
      <div className="cp-section-body">
            <div className="cp-class-members-toolbar">
              <button
                type="button"
                className="cp-class-members-seat-btn"
                onClick={() => setSeatModalOpen(true)}
                disabled={rows.length === 0}
                title={rows.length === 0 ? '멤버를 추가한 뒤 자리배치할 수 있습니다' : '멤버 자리배치'}
              >
                멤버 자리배치
              </button>
              <button type="button" className="cp-class-members-add-btn" onClick={addRow}>
                멤버 추가
              </button>
            </div>
            {duplicateNumbers.size > 0 ? (
              <p className="cp-class-members-number-dup-msg" role="alert">
                번호가 겹쳐요:{' '}
                {Array.from(duplicateNumbers)
                  .sort((a, b) => a - b)
                  .map((n) => `${n}번`)
                  .join(', ')}{' '}
                — 겹치지 않게 바꿔야 저장할 수 있어요.
              </p>
            ) : null}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="cp-class-members-table-wrap">
                {rows.length === 0 ? (
                  <div className="cp-class-members-empty-block">
                    <p className="cp-class-members-empty">
                      등록된 멤버가 없습니다. &quot;멤버 추가&quot;로 추가하거나, 엑셀에서 복사한 목록을 아래 입력란에 붙여 넣어
                      주세요.
                    </p>
                    <input
                      type="text"
                      className="cp-class-members-paste-bridge"
                      placeholder="엑셀에서 이름(탭)성별 열을 복사해 붙여넣기"
                      onPaste={handleEmptyPasteBridge}
                      aria-label="엑셀 목록 붙여넣기"
                    />
                  </div>
                ) : (
                  <table className="cp-class-members-table" aria-label="클래스 멤버 목록">
                    <thead>
                      <tr className="cp-class-members-head-row">
                        <th scope="col" className="cp-class-members-col-select">
                          <button
                            type="button"
                            className={
                              allSelected
                                ? 'cp-class-members-select-circle is-checked'
                                : 'cp-class-members-select-circle'
                            }
                            aria-pressed={allSelected}
                            aria-label={allSelected ? '전체 선택 해제' : '전체 선택'}
                            title={allSelected ? '전체 선택 해제' : '전체 선택'}
                            disabled={deleteChecking || rows.length === 0}
                            onClick={toggleSelectAll}
                          />
                        </th>
                        <th
                          scope="col"
                          className="cp-class-members-col-num"
                          title="번호는 멤버마다 고정돼요(빈 번호 허용). 목록은 번호순으로 정렬돼요."
                        >
                          번호
                        </th>
                        <th scope="col" className="cp-class-members-col-name">
                          <button
                            type="button"
                            className="cp-class-members-head-action-btn"
                            disabled={!bulkActionsEnabled}
                            title={
                              bulkActionsEnabled
                                ? `선택한 ${selectedCount}명의 이름 글자를 초성으로 바꾸기`
                                : '멤버를 선택하면 활성화됩니다'
                            }
                            onClick={openBulkNameModal}
                          >
                            이름
                          </button>
                        </th>
                        <th scope="col" className="cp-class-members-col-gender">
                          <button
                            type="button"
                            className="cp-class-members-head-action-btn"
                            disabled={!bulkActionsEnabled}
                            title={
                              bulkActionsEnabled
                                ? `선택한 ${selectedCount}명의 성별 일괄 입력`
                                : '멤버를 선택하면 활성화됩니다'
                            }
                            onClick={openBulkGenderModal}
                          >
                            성별
                          </button>
                        </th>
                        <th scope="col" className="cp-class-members-col-group">
                          그룹
                        </th>
                        <th scope="col" className="cp-class-members-col-entry">
                          <button
                            type="button"
                            className="cp-class-members-head-action-btn"
                            disabled={!bulkActionsEnabled}
                            title={
                              bulkActionsEnabled
                                ? `선택한 ${selectedCount}명의 입장번호 입력`
                                : '멤버를 선택하면 활성화됩니다'
                            }
                            onClick={openBulkEntryModal}
                          >
                            입장번호
                          </button>
                        </th>
                        <th scope="col" className="cp-class-members-col-profile">
                          프로필
                        </th>
                        <th scope="col" className="cp-class-members-col-drag">
                          <span className="cp-visually-hidden">순서</span>
                        </th>
                        <th scope="col" className="cp-class-members-col-remove">
                          <span className="cp-visually-hidden">삭제</span>
                        </th>
                      </tr>
                    </thead>
                    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                      <tbody>
                        {rows.map((member, index) => (
                          <SortableMemberTableRow
                            key={member.id}
                            id={member.id}
                            orderIndex={index}
                            displayNumber={displayNumbers[index] ?? index + 1}
                            numberDuplicate={duplicateNumbers.has(displayNumbers[index] ?? index + 1)}
                            member={member}
                            groupMembers={rows}
                            workspaceId={workspaceId}
                            gradeId={grade.id}
                            disabled={deleteChecking}
                            selected={selectedMemberIds.has(member.id)}
                            onToggleSelect={() => toggleMemberSelect(member.id)}
                            onNameChange={(name) => updateRow(member.id, { name })}
                            onEntryNumberChange={(entryNumber) =>
                              updateRow(member.id, { entryNumber })
                            }
                            onGenderChange={(gender) => updateRow(member.id, { gender })}
                            onGroupNoChange={(groupNo) => updateRow(member.id, { groupNo })}
                            onProfileImagesChange={(changes) => {
                              if (Object.keys(changes).length === 0) return;
                              persistRows(
                                rows.map((r) =>
                                  changes[r.id] ? { ...r, profileImage: changes[r.id] } : r
                                )
                              );
                            }}
                            onMembersPhotoChange={(nextMembers) => persistRows(nextMembers)}
                            onRemove={() => removeRow(member.id)}
                            onNumberCommit={(n) => commitMemberNumber(member.id, n)}
                            onBulkPasteFromName={handleBulkPasteFromName}
                          />
                        ))}
                      </tbody>
                    </SortableContext>
                  </table>
                )}
              </div>
            </DndContext>
      </div>

      {seatModalOpen && selectedGroupId ? (
        <ClassMemberSeatAssignmentModal
          open={seatModalOpen}
          onClose={() => setSeatModalOpen(false)}
          groupName={group.name}
          members={rows}
          workspaceId={workspaceId}
          gradeId={grade.id}
          groupId={selectedGroupId}
          onSave={async (updatedMembers) => {
            const nextLayout: ClassMemberGradeLayout = {
              ...layout,
              membersByGroupId: {
                ...layout.membersByGroupId,
                [selectedGroupId]: updatedMembers,
              },
            };
            onLayoutChange(nextLayout);
            await onPersistLayout(nextLayout);
          }}
        />
      ) : null}

      {deleteConfirm ? (
        <div
          className="cp-pin-confirm-overlay"
          role="presentation"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="cp-pin-confirm cp-pin-confirm--warn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-cm-delete-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-pin-confirm-warn-icon" aria-hidden>
              !
            </div>
            <p id="cp-cm-delete-confirm-title" className="cp-pin-confirm-warn-title">
              멤버 삭제
            </p>
            <p className="cp-pin-confirm-warn-desc">{deleteConfirm.message}</p>
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--cancel-wide">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => {
                  const pending = deleteConfirm;
                  setDeleteConfirm(null);
                  if (pending.memberId) {
                    applyRemoveMember(pending.memberId);
                  }
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkNameModalOpen
        ? createPortal(
            <div
              className={`cp-pin-confirm-overlay cp-class-members-choseong-overlay${
                document.querySelector('.cp-class-members-settings-modal-panel')
                  ? ''
                  : ' cp-pin-confirm-overlay--viewport'
              }`}
              role="presentation"
              onClick={() => closeChoseongModal(false)}
            >
          <div
            className="cp-pin-confirm cp-class-members-bulk-name-modal cp-class-members-bulk-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-cm-bulk-name-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="cp-cm-bulk-name-title" className="cp-pin-confirm-warn-title">
              이름 초성 바꾸기
            </p>
            <p className="cp-pin-confirm-warn-desc">
              선택한 {selectedCount}명의 이름 표시만 초성으로 바꿉니다. 이미 초성인 글자는 버튼이 켜져
              있고, 다시 누르면 원래 이름으로 돌아옵니다.
            </p>
            <div className="cp-class-members-bulk-name-options" role="group" aria-label="초성으로 바꿀 글자">
              {NAME_CHOSEONG_SLOTS.map((slot) => {
                const active = rows.some(
                  (r) =>
                    selectedMemberIds.has(r.id) &&
                    (r.nameChoseongPositions ?? []).includes(slot.index)
                );
                return (
                  <button
                    key={slot.index}
                    type="button"
                    className={
                      active
                        ? 'cp-class-members-bulk-option is-active'
                        : 'cp-class-members-bulk-option'
                    }
                    aria-pressed={active}
                    onClick={() => toggleBulkNamePosition(slot.index)}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
            <ul className="cp-class-members-bulk-entry-list cp-class-members-bulk-name-preview">
              {rows
                .filter((r) => selectedMemberIds.has(r.id))
                .map((r) => {
                  const nextName = displayClassMemberName(r);
                  return (
                    <li key={r.id}>
                      <span className="cp-class-members-bulk-entry-name">{r.name.trim() || '이름 없음'}</span>
                      <span className="cp-class-members-bulk-name-arrow" aria-hidden="true">
                        →
                      </span>
                      <span className="cp-class-members-bulk-name-result">{nextName.trim() || '이름 없음'}</span>
                    </li>
                  );
                })}
            </ul>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => closeChoseongModal(true)}
              >
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={() => closeChoseongModal(false)}>
                적용
              </button>
            </div>
          </div>
            </div>,
            document.querySelector('.cp-class-members-settings-modal-panel') ?? document.body
          )
        : null}

      {bulkGenderModalOpen ? (
        <div
          className="cp-pin-confirm-overlay"
          role="presentation"
          onClick={() => setBulkGenderModalOpen(false)}
        >
          <div
            className="cp-pin-confirm cp-class-members-bulk-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-cm-bulk-gender-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="cp-cm-bulk-gender-title" className="cp-pin-confirm-warn-title">
              성별 일괄 입력
            </p>
            <p className="cp-pin-confirm-warn-desc">
              선택한 {selectedCount}명에게 적용할 성별을 고르세요.
            </p>
            <div className="cp-class-members-bulk-gender-options" role="group" aria-label="성별 선택">
              <button
                type="button"
                className={
                  bulkGenderDraft === '남'
                    ? 'cp-class-members-bulk-option is-active'
                    : 'cp-class-members-bulk-option'
                }
                aria-pressed={bulkGenderDraft === '남'}
                onClick={() => setBulkGenderDraft('남')}
              >
                남
              </button>
              <button
                type="button"
                className={
                  bulkGenderDraft === '여'
                    ? 'cp-class-members-bulk-option is-active'
                    : 'cp-class-members-bulk-option'
                }
                aria-pressed={bulkGenderDraft === '여'}
                onClick={() => setBulkGenderDraft('여')}
              >
                여
              </button>
            </div>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setBulkGenderModalOpen(false)}
              >
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={applyBulkGender}>
                적용
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bulkEntryModalOpen ? (
        <div
          className="cp-pin-confirm-overlay"
          role="presentation"
          onClick={() => setBulkEntryModalOpen(false)}
        >
          <div
            className="cp-pin-confirm cp-class-members-bulk-entry-modal cp-class-members-bulk-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-cm-bulk-entry-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="cp-cm-bulk-entry-title" className="cp-pin-confirm-warn-title">
              입장번호 입력
            </p>
            <p className="cp-pin-confirm-warn-desc">
              선택한 {bulkEntryPreview.length}명의 입장번호를 확인한 뒤 적용하세요. 일괄입력·랜덤
              생성은 목록에만 반영되며, 적용을 눌러야 저장됩니다.
            </p>
            <div className="cp-class-members-bulk-entry-batch">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={MEMBER_ENTRY_NUMBER_LENGTH}
                className="cp-class-members-input cp-class-members-entry-input cp-class-members-bulk-entry-batch-input"
                value={bulkEntryBatchInput}
                onChange={(e) =>
                  setBulkEntryBatchInput(e.target.value.replace(/\D/g, '').slice(0, MEMBER_ENTRY_NUMBER_LENGTH))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    reflectBulkEntryBatch();
                  }
                }}
                placeholder="6자리 숫자"
                aria-label="일괄 입장번호"
              />
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary cp-class-members-bulk-entry-reflect-btn"
                onClick={reflectBulkEntryBatch}
              >
                반영
              </button>
            </div>
            <button
              type="button"
              className="cp-pin-modal-btn cp-pin-modal-btn--secondary cp-class-members-bulk-entry-random-btn"
              onClick={regenerateBulkEntryPreview}
            >
              랜덤 생성
            </button>
            <ul className="cp-class-members-bulk-entry-list">
              {bulkEntryPreview.map((row) => (
                <li key={row.id}>
                  <span className="cp-class-members-bulk-entry-name">{row.name}</span>
                  <span className="cp-class-members-bulk-entry-num">{row.entryNumber}</span>
                </li>
              ))}
            </ul>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setBulkEntryModalOpen(false)}
              >
                취소
              </button>
              <button type="button" className="cp-btn-primary" onClick={applyBulkEntryNumbers}>
                적용
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
};

function pickSelectedGroupId(
  workspaceId: string,
  gradeId: string,
  layout: ClassMemberGradeLayout
): string | null {
  if (layout.groups.length === 0) return null;
  const ids = new Set(layout.groups.map((g) => g.id));
  const preferred = loadSelectedClassGroupId(workspaceId, gradeId);
  const sorted = [...layout.groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
  );
  return preferred && ids.has(preferred) ? preferred : sorted[0]?.id ?? null;
}

/** 모달이 열리는 첫 프레임에 빈 목록이 보이지 않도록 LS 스냅샷을 동기적으로 읽는다. */
function readCachedClassMemberModalState(workspaceId: string, grades: Grade[]): {
  layouts: Record<string, ClassMemberGradeLayout>;
  selected: Record<string, string | null>;
  ready: boolean;
} {
  const layouts: Record<string, ClassMemberGradeLayout> = {};
  const selected: Record<string, string | null> = {};
  if (!workspaceId || grades.length === 0 || typeof window === 'undefined') {
    return { layouts, selected, ready: false };
  }
  let complete = true;
  for (const grade of grades) {
    const layout = loadClassMemberGradeLayoutFromStorage(workspaceId, grade.id);
    if (!layout) {
      complete = false;
      continue;
    }
    layouts[grade.id] = layout;
    selected[grade.id] = pickSelectedGroupId(workspaceId, grade.id, layout);
  }
  return { layouts, selected, ready: complete };
}

function mergeSelectedGroups(
  prev: Record<string, string | null>,
  layouts: Record<string, ClassMemberGradeLayout>,
  workspaceId: string
): Record<string, string | null> {
  let changed = false;
  const next = { ...prev };
  for (const [gradeId, layout] of Object.entries(layouts)) {
    const pick = pickSelectedGroupId(workspaceId, gradeId, layout);
    const ids = new Set(layout.groups.map((g) => g.id));
    const cur = next[gradeId];
    if (pick == null) {
      if (cur != null) {
        next[gradeId] = null;
        changed = true;
      }
    } else if (cur == null || !ids.has(cur)) {
      if (cur !== pick) {
        next[gradeId] = pick;
        changed = true;
      }
    }
  }
  return changed ? next : prev;
}

const ClassMembersGradeBoard: React.FC<{
  workspaceId: string;
  grade: Grade;
  layout: ClassMemberGradeLayout;
  selectedGroupId: string | null;
  onSelectedGroupIdChange: (groupId: string | null) => void;
  onLayoutChange: (next: ClassMemberGradeLayout) => void;
  onPersistLayout: (next: ClassMemberGradeLayout) => Promise<void>;
  addGroupHost?: HTMLElement | null;
}> = ({
  workspaceId,
  grade,
  layout,
  selectedGroupId,
  onSelectedGroupIdChange,
  onLayoutChange,
  onPersistLayout,
  addGroupHost = null,
}) => {
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [addGroupName, setAddGroupName] = useState('');
  const addGroupInputRef = useRef<HTMLInputElement | null>(null);
  const [groupDeleteChecking, setGroupDeleteChecking] = useState(false);
  const [groupDeleteConfirm, setGroupDeleteConfirm] = useState<{
    groupId: string;
    message: string;
  } | null>(null);

  const sortedGroups = useMemo(
    () => [...layout.groups].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)),
    [layout.groups]
  );
  const activeGroup = sortedGroups.find((g) => g.id === selectedGroupId) ?? sortedGroups[0] ?? null;

  const openAddGroupModal = () => {
    setAddGroupName('');
    setAddGroupOpen(true);
  };

  const submitAddGroup = () => {
    const name = addGroupName.trim();
    if (!name) return;
    const newId = generateGroupId();
    const order = layout.groups.length;
    onLayoutChange({
      groups: [...layout.groups, { id: newId, name, sortOrder: order }],
      membersByGroupId: { ...layout.membersByGroupId, [newId]: [] },
    });
    onSelectedGroupIdChange(newId);
    setAddGroupOpen(false);
    setAddGroupName('');
  };

  useEffect(() => {
    if (!addGroupOpen) return;
    addGroupInputRef.current?.focus();
  }, [addGroupOpen]);

  const applyRemoveGroup = useCallback(
    (groupId: string) => {
      const nextGroups = layout.groups.filter((g) => g.id !== groupId);
      const { [groupId]: _removed, ...restMembers } = layout.membersByGroupId;
      const reindexed = nextGroups.map((g, i) => ({ ...g, sortOrder: i }));
      onLayoutChange({
        groups: reindexed,
        membersByGroupId: restMembers,
      });
      const first = [...reindexed].sort((a, b) => a.sortOrder - b.sortOrder)[0];
      onSelectedGroupIdChange(first?.id ?? null);
    },
    [layout, onLayoutChange, onSelectedGroupIdChange]
  );

  const requestRemoveGroup = (groupId: string) => {
    const target = layout.groups.find((g) => g.id === groupId);
    const groupLabel = target?.name.trim() || '이 학급';
    const memberIds = (layout.membersByGroupId[groupId] ?? []).map((m) => m.id);
    void (async () => {
      setGroupDeleteChecking(true);
      try {
        const summary = await getMemberOwnedDataSummary(workspaceId, grade.id, memberIds);
        setGroupDeleteConfirm({
          groupId,
          message: memberOwnedDataHasContent(summary)
            ? GROUP_OWNED_DATA_DELETE_MESSAGE
            : `「${groupLabel}」 학급과 그 안의 멤버를 삭제할까요?`,
        });
      } catch {
        setGroupDeleteConfirm({
          groupId,
          message: `「${groupLabel}」 학급과 그 안의 멤버를 삭제할까요?`,
        });
      } finally {
        setGroupDeleteChecking(false);
      }
    })();
  };

  const addGroupButton = (
    <button type="button" className="cp-class-members-group-add" onClick={openAddGroupModal}>
      + 학급
    </button>
  );

  return (
    <div className="cp-class-members-grade-row">
      <div className="cp-class-members-group-tabs" role="tablist" aria-label="학급">
        {sortedGroups.map((g) => {
          const active = activeGroup?.id === g.id;
          return (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={'cp-class-members-group-tab' + (active ? ' is-active' : '')}
              onClick={() => onSelectedGroupIdChange(g.id)}
            >
              {g.name}
            </button>
          );
        })}
        {addGroupHost ? null : addGroupButton}
      </div>
      {addGroupHost ? createPortal(addGroupButton, addGroupHost) : null}
      {activeGroup ? (
        <ClassMembersGroupSection
          key={activeGroup.id}
          workspaceId={workspaceId}
          grade={grade}
          layout={layout}
          group={activeGroup}
          groupDeleteChecking={groupDeleteChecking}
          onRequestRemoveGroup={() => requestRemoveGroup(activeGroup.id)}
          onLayoutChange={onLayoutChange}
          onPersistLayout={onPersistLayout}
        />
      ) : (
        <p className="cp-class-members-group-hint">+ 학급으로 학급을 추가하세요.</p>
      )}

      {addGroupOpen
        ? createPortal(
            <div
              className={`cp-pin-confirm-overlay cp-class-members-choseong-overlay${
                document.querySelector('.cp-class-members-settings-modal-panel')
                  ? ''
                  : ' cp-pin-confirm-overlay--viewport'
              }`}
              role="presentation"
              onClick={() => setAddGroupOpen(false)}
            >,
          <div
            className="cp-pin-confirm cp-class-members-group-name-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-cm-add-group-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="cp-cm-add-group-title" className="cp-pin-confirm-warn-title">
              학급 추가
            </p>
            <p className="cp-pin-confirm-warn-desc">새 학급 이름을 입력하세요.</p>
            <input
              ref={addGroupInputRef}
              type="text"
              className="cp-class-members-group-name-input"
              value={addGroupName}
              placeholder="예: 5학년1반"
              aria-label="학급 이름"
              onChange={(e) => setAddGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitAddGroup();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setAddGroupOpen(false);
                }
              }}
            />
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={() => setAddGroupOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={!addGroupName.trim()}
                onClick={submitAddGroup}
              >
                추가
              </button>
            </div>
          </div>
            </div>,
            document.querySelector('.cp-class-members-settings-modal-panel') ?? document.body
          )
        : null}

      {groupDeleteConfirm
        ? createPortal(
            <div
              className={`cp-pin-confirm-overlay cp-class-members-choseong-overlay${
                document.querySelector('.cp-class-members-settings-modal-panel')
                  ? ''
                  : ' cp-pin-confirm-overlay--viewport'
              }`}
              role="presentation"
              onClick={() => setGroupDeleteConfirm(null)}
            >
          <div
            className="cp-pin-confirm cp-pin-confirm--warn"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-cm-group-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-pin-confirm-warn-icon" aria-hidden>
              !
            </div>
            <p id="cp-cm-group-delete-title" className="cp-pin-confirm-warn-title">
              학급 삭제
            </p>
            <p className="cp-pin-confirm-warn-desc">{groupDeleteConfirm.message}</p>
            <div className="cp-pin-confirm-actions cp-pin-confirm-actions--cancel-wide">
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => setGroupDeleteConfirm(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                onClick={() => {
                  const pending = groupDeleteConfirm;
                  setGroupDeleteConfirm(null);
                  applyRemoveGroup(pending.groupId);
                }}
              >
                삭제
              </button>
            </div>
          </div>
            </div>,
            document.querySelector('.cp-class-members-settings-modal-panel') ?? document.body
          )
        : null}
    </div>
  );
};

const ClassMembersPage: React.FC<ClassMembersPageProps> = ({
  workspaceId,
  currentGradeId = '',
  grades = [],
  onSwitchToClassIntegrated: _onSwitchToClassIntegrated,
  embeddedInModal = false,
  onDirtyChange,
  addGroupHost = null,
  saveActionsRef: externalSaveActionsRef,
}) => {
  const [pageAddHost, setPageAddHost] = useState<HTMLDivElement | null>(null);
  const resolvedAddHost = embeddedInModal ? addGroupHost : pageAddHost;
  const { toastState: saveToastState, showSaving, showSuccess, showError } = useAutoStatusToast(3000);
  const { setClassMembersDirty, saveClassMembersRef } = useContext(GradeTrackLeaveContext);
  const [initialCache] = useState(() => readCachedClassMemberModalState(workspaceId, grades));
  const [layoutsByGrade, setLayoutsByGrade] = useState(initialCache.layouts);
  const [selectedGroupByGrade, setSelectedGroupByGrade] = useState(initialCache.selected);
  const [snapshotReady, setSnapshotReady] = useState(initialCache.ready);
  const lastSavedSnapshotRef = useRef<string>(
    initialCache.ready ? JSON.stringify(initialCache.layouts) : ''
  );
  const getSnapshotRef = useRef<() => string>(() => '');

  const getSnapshot = useCallback(() => JSON.stringify(layoutsByGrade), [layoutsByGrade]);
  getSnapshotRef.current = getSnapshot;
  const isDirty = snapshotReady && lastSavedSnapshotRef.current !== '' && getSnapshot() !== lastSavedSnapshotRef.current;

  useEffect(() => {
    if (!workspaceId || !grades.length) {
      setLayoutsByGrade({});
      setSelectedGroupByGrade({});
      lastSavedSnapshotRef.current = '';
      setSnapshotReady(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const next = await getClassMemberGradeLayoutsMap(
          workspaceId,
          grades.map((g) => g.id)
        );
        if (cancelled) return;
        const serialized = JSON.stringify(next);
        const saved = lastSavedSnapshotRef.current;
        const edited = saved !== '' && getSnapshotRef.current() !== saved;
        if (!edited) {
          lastSavedSnapshotRef.current = serialized;
          setLayoutsByGrade((prev) => (JSON.stringify(prev) === serialized ? prev : next));
          setSelectedGroupByGrade((prev) => mergeSelectedGroups(prev, next, workspaceId));
        }
        setSnapshotReady(true);
      } catch {
        if (cancelled) return;
        if (lastSavedSnapshotRef.current) {
          setSnapshotReady(true);
          return;
        }
        setLayoutsByGrade({});
        setSelectedGroupByGrade({});
        lastSavedSnapshotRef.current = '';
        setSnapshotReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, grades]);

  useEffect(() => {
    if (!snapshotReady || !grades.length) return;
    setSelectedGroupByGrade((prev) => mergeSelectedGroups(prev, layoutsByGrade, workspaceId));
  }, [snapshotReady, layoutsByGrade, grades, workspaceId]);

  useEffect(() => {
    return subscribeSelectedClassGroupChanged((detail) => {
      if (detail.workspaceId !== workspaceId.trim()) return;
      setSelectedGroupByGrade((prev) => {
        const layout = layoutsByGrade[detail.gradeId] ?? emptyGradeLayout();
        const ids = new Set(layout.groups.map((g) => g.id));
        const nextId =
          detail.groupId && ids.has(detail.groupId)
            ? detail.groupId
            : prev[detail.gradeId] ?? null;
        if (prev[detail.gradeId] === nextId) return prev;
        return { ...prev, [detail.gradeId]: nextId };
      });
    });
  }, [workspaceId, layoutsByGrade]);

  const onLayoutChangeForGrade = useCallback((gradeId: string, next: ClassMemberGradeLayout) => {
    setLayoutsByGrade((prev) => ({ ...prev, [gradeId]: next }));
  }, []);

  const persistLayoutForGrade = useCallback(
    async (gradeId: string, next: ClassMemberGradeLayout) => {
      await replaceGradeMemberLayout(workspaceId, gradeId, next);
      setLayoutsByGrade((prev) => {
        const merged = { ...prev, [gradeId]: next };
        lastSavedSnapshotRef.current = JSON.stringify(merged);
        return merged;
      });
      setSnapshotReady(true);
    },
    [workspaceId]
  );

  const handleDiscardUnsaved = useCallback(() => {
    const raw = lastSavedSnapshotRef.current;
    if (!raw) return;
    try {
      const next = JSON.parse(raw) as Record<string, ClassMemberGradeLayout>;
      setLayoutsByGrade(next);
    } catch {
      return;
    }
  }, []);

  const handleSaveAll = useCallback(async () => {
    // 같은 그룹 안 번호 중복은 저장하지 않는다(모달은 열린 채로 유지되도록 throw).
    const dupMessages: string[] = [];
    for (const grade of grades) {
      const layout = layoutsByGrade[grade.id];
      if (!layout) continue;
      for (const g of layout.groups) {
        const dups = findDuplicateMemberNumbers(layout.membersByGroupId[g.id] ?? []);
        if (dups.size === 0) continue;
        const nums = Array.from(dups)
          .sort((a, b) => a - b)
          .map((n) => `${n}번`)
          .join(', ');
        dupMessages.push(`${(g.name ?? '').trim() || '학급'}: ${nums}`);
      }
    }
    if (dupMessages.length > 0) {
      const message = `번호가 겹쳐 저장할 수 없어요 (${dupMessages.join(' / ')})`;
      showError(message);
      throw new Error(message);
    }
    showSaving();
    try {
      await Promise.all(
        grades.map((grade) =>
          replaceGradeMemberLayout(
            workspaceId,
            grade.id,
            materializeLayoutMemberNumbers(layoutsByGrade[grade.id] ?? emptyGradeLayout())
          )
        )
      );
      lastSavedSnapshotRef.current = getSnapshotRef.current();
      setSnapshotReady(true);
      showSuccess();
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : String(error));
    }
  }, [grades, layoutsByGrade, workspaceId, showError, showSaving, showSuccess]);

  useEffect(() => {
    if (embeddedInModal) {
      onDirtyChange?.(isDirty);
      return;
    }
    setClassMembersDirty(isDirty);
    return () => setClassMembersDirty(false);
  }, [embeddedInModal, isDirty, onDirtyChange, setClassMembersDirty]);

  useEffect(() => {
    const actions = { save: handleSaveAll, discard: handleDiscardUnsaved };
    if (embeddedInModal) {
      if (externalSaveActionsRef) externalSaveActionsRef.current = actions;
      return () => {
        if (externalSaveActionsRef) externalSaveActionsRef.current = null;
      };
    }
    saveClassMembersRef.current = actions;
    return () => {
      saveClassMembersRef.current = null;
    };
  }, [
    embeddedInModal,
    externalSaveActionsRef,
    handleSaveAll,
    handleDiscardUnsaved,
    saveClassMembersRef,
  ]);

  const visibleGrades = useMemo(() => {
    if (!currentGradeId) return grades;
    return grades.filter((g) => g.id === currentGradeId);
  }, [grades, currentGradeId]);

  return (
    <section
      className={
        embeddedInModal
          ? 'cp-timetable-hub-page cp-timetable-hub-page--embedded cp-class-members-page--modal'
          : 'cp-timetable-hub-page'
      }
    >
      {!embeddedInModal ? (
        <div className="cp-page-title-row">
          <div>
            <h2>클래스 멤버</h2>
          </div>
          <div className="cp-class-members-page-title-actions">
            {grades.length === 0 ? (
              <button type="button" className="cp-class-members-group-add" disabled>
                + 학급
              </button>
            ) : (
              <div ref={setPageAddHost} className="cp-class-members-header-add-slot" />
            )}
            <button type="button" className="cp-btn-save-all" onClick={() => void handleSaveAll()} disabled={!isDirty}>
              {isDirty ? '저장하기' : '변경사항 없음'}
            </button>
          </div>
        </div>
      ) : null}

      {grades.length === 0 ? (
        <div className="cp-class-members-grade-row">
        <article className="cp-card cp-class-members-section">
          <div className="cp-track-section-header cp-grade-track-header-row cp-class-members-section-header">
            <div className="cp-class-members-section-header-title-row">
              <div className="card-summary pill cp-track-class-pill">클래스 단독</div>
              <h3>클래스 멤버</h3>
            </div>
            <span className="cp-section-class-empty-hint cp-class-members-empty-hint-stacked">
              <span className="cp-class-members-empty-hint-mark" aria-hidden="true">
                *
              </span>
              <span className="cp-class-members-empty-hint-lines">
                <span className="cp-class-members-empty-hint-line">클래스를 등록해주세요.</span>
                <span className="cp-class-members-empty-hint-line">클래스를 등록하면 섹션이 활성화 됩니다.</span>
              </span>
            </span>
          </div>
          <div className="cp-section-body cp-section-body-disabled">
            <p className="cp-class-members-group-hint">클래스 등록 후 학급을 만들 수 있습니다.</p>
          </div>
        </article>
        </div>
      ) : currentGradeId !== '' && visibleGrades.length === 0 ? (
        <article className="cp-card">
          <div className="cp-track-section-header">
            <h3>클래스 멤버</h3>
          </div>
          <p className="page-desc">선택한 클래스를 찾을 수 없습니다. 상단에서 다른 클래스를 선택해 주세요.</p>
        </article>
      ) : (
        <div className="cp-class-members-columns">
          {visibleGrades.map((grade) => {
            const layout = layoutsByGrade[grade.id];
            if (!layout && !snapshotReady) return null;
            return (
              <ClassMembersGradeBoard
                key={grade.id}
                workspaceId={workspaceId}
                grade={grade}
                layout={layout ?? emptyGradeLayout()}
                selectedGroupId={selectedGroupByGrade[grade.id] ?? null}
                onSelectedGroupIdChange={(groupId) => {
                  setSelectedGroupByGrade((prev) => ({ ...prev, [grade.id]: groupId }));
                  saveSelectedClassGroupId(workspaceId, grade.id, groupId);
                }}
                onLayoutChange={(next) => onLayoutChangeForGrade(grade.id, next)}
                onPersistLayout={(next) => persistLayoutForGrade(grade.id, next)}
                addGroupHost={resolvedAddHost}
              />
            );
          })}
        </div>
      )}
      {!embeddedInModal ? (
        <button
          type="button"
          className="cp-fab-save"
          onClick={() => void handleSaveAll()}
          disabled={!isDirty}
          aria-label="저장하기"
          title="저장하기"
        >
          <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
        </button>
      ) : null}
      {saveToastState ? <AutoStatusToast state={saveToastState} /> : null}
    </section>
  );
};

export default ClassMembersPage;
